import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

class RebuyRecommendationsComponent extends Component {
  requiredRefs = ['recommendationsResults', 'placeholder'];
  #controller = new AbortController();

  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

    /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  get recommendationUrl() {
    const { widgetId, rebuyApiKey, productId, maxProducts } = this.dataset;

    return `https://rebuyengine.com/api/v1/custom/id/${widgetId}?key=${rebuyApiKey}&shopify_product_ids=${productId}&limit=${maxProducts}`
  }

  async #loadRecommendations() {
    const { recommendationsPerformed, widgetId } = this.dataset;

    if (!widgetId) return;

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    const recommendationsMarkup = await this.#getRecommendationsProductsMarkup()

    const parsedMarkup = new DOMParser().parseFromString(recommendationsMarkup, 'text/html');
    const resultProductCards = parsedMarkup.querySelectorAll('[ref="recentlyViewedItems[]"]')

    const resultsMarkup = Array.from(resultProductCards).map(card => {
      card.querySelectorAll('[href]').forEach(link => {
        const href = link.href
        const url = new URL(href)
        const productHandle = url.pathname.split('products/').pop()

        url.searchParams.set('_rdiscovery-handle', productHandle)
        url.searchParams.set('_rdiscovery-widget', this.dataset.widgetId)

        link.setAttribute('href', url.toString())
      })
      return `<div class="resource-list__item">${card.innerHTML}</div>`
    })
      .join('')

    this.querySelector('[ref="recommendationsResults"]').innerHTML = resultsMarkup
    this.querySelectorAll('.card-gallery').forEach(el => {
      el.style.setProperty('--gallery-aspect-ratio', this.dataset.imageRatio);
    })
    this.dataset.recommendationsPerformed = 'true';
  }

  async #getRecommendationsProductsMarkup() {
    const productIds = await this.getRecommendationProductIds()

    if (productIds.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  async getRecommendationProductIds() {
    const response = await fetch(this.recommendationUrl)
      .then(res => res.json())

      return (response.data || []).map(item => item.id)
  }
}

if (!customElements.get('rebuy-recommendations-component')) {
  customElements.define('rebuy-recommendations-component', RebuyRecommendationsComponent);
}
